import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Play, Pause, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  lessonId: string;
  lessonTitle: string;
}

const MAX_SECONDS = 60;

const fmt = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const LessonPreviewPlayer = ({ open, onOpenChange, lessonId, lessonTitle }: Props) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      setPreviewUrl(null);
      setPlaying(false);
      setCurrent(0);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("lesson-preview", {
          body: { lesson_id: lessonId },
        });
        if (cancelled) return;
        if (error || !data?.preview_url) throw new Error(error?.message || "Nepavyko gauti peržiūros");
        setPreviewUrl(data.preview_url);
      } catch (e: any) {
        toast.error(e.message ?? "Nepavyko paleisti peržiūros");
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, lessonId, onOpenChange]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().catch(() => {});
    } else {
      a.pause();
    }
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.currentTime >= MAX_SECONDS) {
      a.pause();
      a.currentTime = MAX_SECONDS;
      setCurrent(MAX_SECONDS);
      setPlaying(false);
      return;
    }
    setCurrent(a.currentTime);
  };

  const goBuy = () => {
    onOpenChange(false);
    navigate(`/pamokos?lesson=${lessonId}`);
  };

  const pct = Math.min(100, (current / MAX_SECONDS) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-cream-soft border-ink/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-marquee text-2xl text-ink">{lessonTitle}</DialogTitle>
          <DialogDescription className="text-ink/65">
            Pirma minutė — nemokama peržiūra.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-5 py-4">
          {loading && (
            <div className="flex items-center gap-2 text-ink/65 text-sm py-8">
              <Loader2 size={16} className="animate-spin" /> Ruošiama peržiūra...
            </div>
          )}
          {!loading && previewUrl && (
            <>
              <Button
                onClick={toggle}
                className="h-16 w-16 rounded-full bg-ink text-cream hover:bg-ink/85"
                aria-label={playing ? "Pauzė" : "Groti"}
              >
                {playing ? <Pause size={22} /> : <Play size={22} className="ml-0.5" />}
              </Button>

              {/* Custom progress bar capped at 60s */}
              <div className="w-full">
                <div className="h-1.5 w-full rounded-full bg-ink/15 overflow-hidden">
                  <div
                    className="h-full bg-ink transition-[width] duration-150 ease-linear"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-ink/60 mt-1.5 font-mono">
                  <span>{fmt(current)}</span>
                  <span>1:00</span>
                </div>
              </div>

              <audio
                ref={audioRef}
                src={previewUrl}
                onTimeUpdate={onTime}
                onEnded={() => setPlaying(false)}
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                preload="metadata"
                className="hidden"
              />

              <Button
                onClick={goBuy}
                className="w-full h-12 rounded-full bg-terracotta text-cream hover:bg-terracotta/90 font-semibold"
              >
                Įsigyti pilną pamoką
                <ArrowRight size={16} className="ml-1" />
              </Button>

              <p className="text-xs text-ink/50 text-center whitespace-pre-line">
                {"\n"}
              </p>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LessonPreviewPlayer;
